// componentStack is string | null in React 18, string | null | undefined in React 19 — accept both
type QueuedError = { error: unknown; info?: { componentStack?: string | null | undefined } };

const errorQueue: QueuedError[] = [];
let initStarted = false;
let drained = false;

export function reportError(error: unknown, info?: QueuedError["info"]): void {
  if (drained) {
    void import("@sentry/react").then((Sentry) => Sentry.captureException(error));
    return;
  }
  errorQueue.push({ error, ...(info !== undefined && { info }) });
}

export function initSentryDeferred(): void {
  if (initStarted) return;
  initStarted = true;
  if (!import.meta.env.PROD) return;
  if (!import.meta.env.VITE_SENTRY_DSN) return;

  void Promise.all([
    import("@sentry/react"),
    import("react"),
    import("react-router"),
  ]).then(([Sentry, React, RR]) => {
    // Drain the queue regardless of init success/failure — dropping queued
    // errors is worse than forwarding them to a partially-initialized Sentry.
    // Sentry.init() returns Client | undefined (not a Promise); wrap in
    // try/catch/finally to handle synchronous throws.
    try {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        tracesSampleRate: 0.1,
        sendDefaultPii: false,
        integrations: [
          Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation: RR.useLocation,
            useNavigationType: RR.useNavigationType,
            createRoutesFromChildren: RR.createRoutesFromChildren,
            matchRoutes: RR.matchRoutes,
          }),
        ],
      });
    } catch (err) {
      console.error("Sentry init failed:", err);
    } finally {
      drained = true;
      const pending = errorQueue.splice(0);
      for (const queued of pending) {
        Sentry.captureException(queued.error);
      }
    }
  }).catch((err: unknown) => {
    console.error("Sentry dynamic import failed:", err);
  });
}

export function scheduleSentryInit(): void {
  if (typeof window === "undefined") return;
  const idle = (window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  }).requestIdleCallback;
  if (idle) {
    idle(() => initSentryDeferred(), { timeout: 2000 });
  } else {
    setTimeout(initSentryDeferred, 200);
  }
}
